import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'md' | 'lg' | 'xl' | '2xl';
}

const sizes = {
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-5xl',
};

export function Modal({ open, onClose, title, children, size = 'lg' }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div className="absolute inset-0 bg-overlay/45" onClick={onClose} />
      <div
        className={cn(
          'relative w-full bg-surface rounded-lg border border-ink-200 shadow-2xl overflow-hidden animate-slide-up',
          sizes[size],
        )}
      >
        {title && (
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-ink-200">
            <h3 id="modal-title" className="font-semibold text-ink-900 text-[15px] tracking-[-0.01em]">
              {title}
            </h3>
            <button
              onClick={onClose}
              className="w-8 h-8 inline-flex items-center justify-center rounded-md text-ink-500 hover:text-ink-900 hover:bg-ink-100 transition-colors"
              aria-label="Stäng"
            >
              <X size={17} strokeWidth={2} />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
